import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, of, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { BaseService } from '../base/base.service';
import { APIROUTES } from 'src/app/shared/constants';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { Device } from '@capacitor/device';
import { ModalController } from '@ionic/angular';
import { ToastService } from '../toast-service/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { LoaderService } from '../loader-service/loader.service';
import { fetchSignInMethodsForEmail, getAuth } from 'firebase/auth';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  public isReady = new BehaviorSubject<boolean>(false);

  private currentUserSubject = new BehaviorSubject<any>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    private baseService: BaseService,
    private modalController : ModalController,
    private toastservice: ToastService,
    private translate: TranslateService,
    private router: Router,
    private loaderService: LoaderService
  ) { }

  //individual registration
  public individualRegistration(body: any){
    return this.baseService.post(environment.baseUrl + APIROUTES.PROFILE.CREATE, body).pipe(catchError(this.errorHandler));
  }

  //pmd registration
  public pmdRegistration(body: any){
    return this.baseService.post(environment.baseUrl + APIROUTES.PMD.PROFILE.CREATE, body).pipe(catchError(this.errorHandler));
  }

  //org registration
  public organizationRegistration(body: any) {
    return this.baseService.post(environment.baseUrl + APIROUTES.ORGANIZATION.PROFILE.CREATE, body).pipe(catchError(this.errorHandler));
  }

  public login(body: any){
    return this.baseService.post(environment.baseUrl + APIROUTES.AUTH.LOGIN, body).pipe(
      tap((response: any) => {
        if (response && response.success && response.value?.user) {
          localStorage.setItem("world_prays_token", response.value.token);
          localStorage.setItem("token_issued_date", response.value.issued_time || new Date().toISOString());
          localStorage.setItem("token_expiration_date", response.value.expiration_time);
          localStorage.setItem("world_prays_user", JSON.stringify(response.value.user));
          localStorage.setItem("world_prays_is_guest", "0");
          this.currentUserSubject.next(response.value.user);
        }
      }),
      catchError(this.errorHandler)
    );
  }

  public registerDevideID(body: any){
    return this.baseService.post(environment.baseUrl + APIROUTES.AUTH.REGISTER_DEVICE_ID_FOR_GUEST, body).pipe(
      tap((response : any) =>{
        if(response && response.success && response.value?.user)
        {
          localStorage.setItem("world_prays_token", response.value.token);
          localStorage.setItem("token_issued_date", response.value.issued_time || new Date().toISOString());
          localStorage.setItem("token_expiration_date", response.value.expiration_time);
          localStorage.setItem("world_prays_user", JSON.stringify(response.value.user));
          this.currentUserSubject.next(response.value.user);
        }
      }),
      catchError(this.errorHandler)
    );
  }

  public firebaseAuth(token : string, devideId : string, password? : string)
  {
    console.log({FIREBASE_TOKEN_BACKEND : token});
    console.log({DEVICE_ID_BACKEND : devideId});
    console.log({URL_API : environment.baseUrl + APIROUTES.AUTH.FIREBASE_AUTH});
    return this.baseService.post(environment.baseUrl + APIROUTES.AUTH.FIREBASE_AUTH, {token : token, device_id : devideId, password : password}).pipe(
      tap((response: any) => {
        if (response && response.success && response.value?.user) {
          localStorage.setItem("world_prays_token", response.value.token);
          localStorage.setItem("token_issued_date", response.value.issued_time || new Date().toISOString());
          localStorage.setItem("token_expiration_date", response.value.expiration_time);
          localStorage.setItem("world_prays_user", JSON.stringify(response.value.user));
          localStorage.setItem("world_prays_is_guest", "0");
          this.currentUserSubject.next(response.value.user);
        }
      }),
      catchError(this.errorHandler)
    );
  }

  public clearLocalStorage() {
    [
      'world_prays_registered_user_data',
      'world_prays_registered_pmd_data',
      'world_prays_registered_org_data',
      'world_prays_token',
      'organization_registration',
      'profile_registration',
      'pmd_registration',
      'world_prays_user',
      'token_issued_date',
      'prayerStarted',
      'firebaseToken',
      'start_time',
      'token_expiration_date',
      'world_prays_is_guest'
    ].forEach(
      (key) => {
        localStorage.removeItem(key);
      }
    );
  }

  public logout(){
    this.currentUserSubject.next(null);
    return this.baseService.post(environment.baseUrl + APIROUTES.AUTH.LOGOUT,{}).pipe(catchError(this.errorHandler));
  }

  public me(){
    return this.baseService.get(environment.baseUrl + APIROUTES.AUTH.ME,).pipe(
      tap((response : any)=> {
        if (response && response.success) {
          this.currentUserSubject.next(response.value);
        }
      }),
      catchError(this.errorHandler)
    );
  }

  public updateCurrentUserField(user: any) {
    this.currentUserSubject.next(user);
  }

  public getCurrentUserValue(): any {
    return this.currentUserSubject.value;
  }

  public refreshToken(){
    return this.baseService.post(environment.baseUrl + APIROUTES.AUTH.REFRESH, {}).pipe(catchError(this.errorHandler));
  }

  public forgotPassword(email: any) {
    return this.baseService.post(environment.baseUrl + APIROUTES.AUTH.PASSWORD.FORGOT, email).pipe(catchError(this.errorHandler));
  }

  public updatePassword(body : any) {
    return this.baseService.put(environment.baseUrl + APIROUTES.AUTH.PASSWORD.UPDATE, body).pipe(catchError(this.errorHandler));
  }

  public resetPassword(body: any) {
    return this.baseService.post(environment.baseUrl + APIROUTES.AUTH.PASSWORD.RESET, body).pipe(catchError(this.errorHandler));
  }

  public deactivateAccount(body: any){
    return  this.baseService.post(environment.baseUrl + APIROUTES.PROFILE.DEACTIVATEACCOUNT, body).pipe(catchError(this.errorHandler));
  }

  public linkUserToOrganization (organizationID : number)
  {
    return this.baseService.post(environment.baseUrl + APIROUTES.AUTH.LINK_TO_ORGANIZATION, {organization_id : organizationID}).pipe(catchError(this.errorHandler));
  }

  public linkUserToPMD (pmdID : number)
  {
    return this.baseService.post(environment.baseUrl + APIROUTES.AUTH.LINK_TO_PMD, {pmd_id : pmdID}).pipe(catchError(this.errorHandler));
  }

  public getUserTotalHourPerMonth(month_year : string)
  {
    return this.baseService.get(environment.baseUrl + APIROUTES.AUTH.USER_TOTAL_HOUR_PER_MONTH+'?current_month_year='+month_year).pipe(catchError(this.errorHandler));
  }

  public getOrgTotalHourPerMonth(month_year : string)
  {
    return this.baseService.get(environment.baseUrl + APIROUTES.AUTH.ORG_TOTAL_HOUR_PER_MONTH+'?month_year='+month_year).pipe(catchError(this.errorHandler));
  }

  // ----------------------------------------------------
  // 1. CONNEXION GOOGLE NATIVE
  // ----------------------------------------------------
  async loginWithGoogle() {
    try {
      const result = await FirebaseAuthentication.signInWithGoogle();
      console.log({RESULT : result});
      const currentUser = await FirebaseAuthentication.getCurrentUser();

      const info = await Device.getId();
      
      if (currentUser) {
        const idTokenResult = await FirebaseAuthentication.getIdToken();
        const firebaseToken = idTokenResult.token;

        console.log({FIREBASE_TOKEN : firebaseToken});
        console.log({DEVICE_ID : info.identifier});
        this.loaderService.show('common.please_wait');
        this.firebaseAuth(firebaseToken,info.identifier).subscribe(
          async (response : any) => {
            console.log({RESPONSE : response});
            this.loaderService.dismiss();
            if(response.success)
            {
              localStorage.setItem("world_prays_user", JSON.stringify(response.value.user));
              localStorage.setItem("world_prays_token", response.value.token)
              localStorage.setItem("token_issued_date", response.value.issued_time || new Date().toISOString())
              localStorage.setItem("token_expiration_date", response.value.expiration_time);
              localStorage.setItem("world_prays_is_guest", "0");

              this.isReady.next(true);
              // dismiss modal
              this.modalController.dismiss();
              // redirect to pray for all page
              this.router.navigate(['tabs/prayer-for-all']);
            }
            else{
              this.toastservice.message(this.translate.instant('common.error') + ': ' + response.message, 'danger');
            }
          }
        );
      }
    } catch (error : any) {
      console.log('Google Sign-In error:', error);
      const errorCode = error?.code || this.extractErrorCode(error?.message);

      console.log('Code extract :', errorCode);
      this.displayCustomAuthMessage(errorCode,error);
    }
  }

  // ----------------------------------------------------
  // 2. CONNEXION APPLE NATIVE
  // ----------------------------------------------------
  async loginWithApple() {
    try {
      const result = await FirebaseAuthentication.signInWithApple();
      console.log({RESULT : result});
      const currentUser = await FirebaseAuthentication.getCurrentUser();

      const info = await Device.getId();
      
      if (currentUser) {
        const idTokenResult = await FirebaseAuthentication.getIdToken();
        const firebaseToken = idTokenResult.token;

        console.log({FIREBASE_TOKEN : firebaseToken});
        console.log({DEVICE_ID : info.identifier});
        this.loaderService.show('common.please_wait');
        this.firebaseAuth(firebaseToken,info.identifier).subscribe(
          async (response : any) => {
            console.log({RESPONSE : response});
            this.loaderService.dismiss();
            if(response.success)
            {
              localStorage.setItem("world_prays_user", JSON.stringify(response.value.user));
              localStorage.setItem("world_prays_token", response.value.token)
              localStorage.setItem("token_issued_date", response.value.issued_time || new Date().toISOString())
              localStorage.setItem("token_expiration_date", response.value.expiration_time);
              localStorage.setItem("world_prays_is_guest", "0");
              
              this.isReady.next(true);
              // dismiss modal
              this.modalController.dismiss();
              // redirect to pray for all page
              this.router.navigate(['tabs/prayer-for-all']);
            }
            else{
              this.toastservice.message(this.translate.instant('common.error') + ': ' + response.message, 'danger');
            }
          }
        );
      }
    } catch (error : any) {
      console.log('Apple Sign-In error:', error);
      const errorCode = error?.code || this.extractErrorCode(error?.message);

      console.log('Code extract :', errorCode);
      this.displayCustomAuthMessage(errorCode,error);
    }
  }

  async loginWithFacebook() {
    try {
      const result = await FirebaseAuthentication.signInWithFacebook();
      console.log({RESULT : result});
      const currentUser = await FirebaseAuthentication.getCurrentUser();

      const info = await Device.getId();
      
      if (currentUser) {
        const idTokenResult = await FirebaseAuthentication.getIdToken();
        const firebaseToken = idTokenResult.token;

        console.log({FIREBASE_TOKEN : firebaseToken});
        console.log({DEVICE_ID : info.identifier});
        this.loaderService.show('common.please_wait');
        this.firebaseAuth(firebaseToken,info.identifier).subscribe(
          async (response : any) => {
            console.log({RESPONSE : response});
            this.loaderService.dismiss();
            if(response.success)
            {
              localStorage.setItem("world_prays_user", JSON.stringify(response.value.user));
              localStorage.setItem("world_prays_token", response.value.token)
              localStorage.setItem("token_issued_date", response.value.issued_time || new Date().toISOString())
              localStorage.setItem("token_expiration_date", response.value.expiration_time);
              localStorage.setItem("world_prays_is_guest", "0");
              
              this.isReady.next(true);
              // dismiss modal
              this.modalController.dismiss();
              // redirect to pray for all page
              this.router.navigate(['tabs/prayer-for-all']);
            }
            else{
              this.toastservice.message(this.translate.instant('common.error') + ': ' + response.message, 'danger');
            }
          }
        );
      }
    } catch (error : any) {
      console.log('Facebook Sign-In error:', error);
      const errorCode = error?.code || this.extractErrorCode(error?.message);

      console.log('Code extract :', errorCode);
      this.displayCustomAuthMessage(errorCode,error);
    }
  }

  //Method to handle error
  errorHandler(error:any) {
    return of(error);
  }

  private extractErrorCode(message: string): string {
    if (!message) return 'common.error';
    const match = message.match(/\((auth\/[a-z-]+)\)/);
    return match ? match[1] : message;
  }

  async displayCustomAuthMessage(errorCode : string, error : any)
  {
    if (errorCode === 'auth/account-exists-with-different-credential') {
      const email = error?.customData?.email || error?.email;
      console.log({EMAIL : email});
      if (email) {
        
        const auth = getAuth();
        console.log({AUTHH : auth});
        const methods = await fetchSignInMethodsForEmail(auth, email);
        console.log('Méthodes de connexion existantes pour cet email:', methods);

        let providerName = 'Google';
        if (methods.includes('apple.com')) {
          providerName = 'Apple';
        } else if (methods.includes('google.com')) {
          providerName = 'Google';
        } else if (methods.includes('password')) {
          providerName = 'Email / Password';
        } else if (methods.includes('facebook.com')) {
          providerName = 'Facebook';
        }
        this.toastservice.message( this.translate.instant('account_exists_with_different_credential',{email : email, providername : providerName }), 'warning');
      }
    }
    else{
      this.toastservice.message(this.translate.instant(errorCode), 'danger');
    }
  }
}